import React, { useReducer, useState } from "react";
import PropTypes from "prop-types";
import { Input, Select, Statistic } from "antd";
import { useFetch } from "../../../Utils/useFetch";
import { FETCH_PROBLEMATIC_STOCKS_API, FETCH_ALL_COUNTER_API } from "../../../Constants/Apis";
import { reducer } from "../../../Utils/CommonUtils";
import ProblematicStockTable from "../../elements/Tables/ProblematicStockTable/ProblematicStockTable";

const { Search } = Input;
const { Option } = Select;

const ProblematicHistoryPage = ({ history, userData }) => {
  const initialState = {
    searchKeyword: "",
    filteredInfo: {},
    sorter: {},
    current: 1,
    pageSize: 10,
  };

  const [state, dispatch] = useReducer(reducer, initialState);
  const [counterFilter, setCounterFilter] = useState(undefined);

  const { fetchedData, callReFetch, loading } = useFetch(FETCH_PROBLEMATIC_STOCKS_API, state);

  // Extract table data + pagination
  const stockData = fetchedData?.data?.data || [];
  const paginationInfo = fetchedData?.data?.pagination || {
    current: 1,
    pageSize: 10,
    total: 0,
  };

  // Calculate totals
  const totalItems = paginationInfo?.total ?? 0;
  
  // Calculate total quantity from all data (we'll need to fetch all data for this)
  const totalQuantityAll = Array.isArray(stockData)
    ? stockData.reduce((sum, row) => sum + (Number(row?.receivedQuantity) || 0), 0)
    : 0;
    
  const totalQuantityThisPage = Array.isArray(stockData)
    ? stockData.reduce((sum, row) => sum + (Number(row?.receivedQuantity) || 0), 0)
    : 0;

  // Fetch counters for filter options
  const { fetchedData: counterFetched = {} } = useFetch(FETCH_ALL_COUNTER_API, state);
  const counterData = counterFetched?.data || [];

  // Handlers
  const handleSearch = (value) => {
    dispatch({ type: "current", payload: 1 });
    dispatch({ type: "searchKeyword", payload: value });
    callReFetch();
  };

  const handlePaginationChange = (page, pageSize) => {
    dispatch({ type: "current", payload: page });
    dispatch({ type: "pageSize", payload: pageSize });
    callReFetch();
  };

  const handleCounterFilter = (value) => {
    setCounterFilter(value);
    dispatch({
      type: "filteredInfo",
      payload: {
        ...state.filteredInfo,
        counterCode: value || undefined,
      },
    });
    dispatch({ type: "current", payload: 1 }); // reset page
    callReFetch();
  };

  const handleTableChange = (pagination, filters, sorter) => {
    dispatch({ type: "current", payload: pagination.current });
    dispatch({ type: "pageSize", payload: pagination.pageSize });
    dispatch({ type: "filteredInfo", payload: filters });
    dispatch({ type: "sorter", payload: sorter });
    callReFetch();
  };

  if (userData?.username !== "leo") {
    return null;
  }

  return (
    <div
      style={{
        padding: "24px",
        backgroundColor: "#f5f5f5",
        minHeight: "100vh",
      }}
    >
      <div
        style={{
          backgroundColor: "white",
          padding: "24px",
          borderRadius: "8px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        }}
      >
        <div
          style={{
            marginBottom: "24px",
          }}
        >
          <h1 style={{ margin: 0, fontSize: "24px", fontWeight: "bold" }}>
            Problematic History
          </h1>
        </div>

        <div
          style={{
            display: "flex",
            gap: "16px",
            marginBottom: "24px",
            flexWrap: "wrap",
          }}
        >
          <div style={{ flex: "1", minWidth: "300px" }}>
            <Search
              placeholder="Search by barcode, article, color, noSJ, counter code, counter name..."
              allowClear
              enterButton="Search"
              size="large"
              onSearch={handleSearch}
              style={{ width: "100%" }}
            />
          </div>
          <div style={{ minWidth: "200px" }}>
            <Select
              placeholder="Filter by Counter"
              style={{ width: "100%" }}
              size="large"
              value={counterFilter}
              onChange={handleCounterFilter}
              allowClear
              showSearch
              filterOption={(input, option) =>
                String(option.children).toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
            >
              {counterData.map((counter) => (
                <Option key={counter.code} value={counter.code}>
                  {counter.name} ({counter.code})
                </Option>
              ))}
            </Select>
          </div>
        </div>

        {/* Statistics */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
            gap: "16px",
            marginBottom: "24px",
          }}
        >
          <Statistic
            title="Total Items"
            value={totalItems}
            valueStyle={{ color: "#1890ff" }}
          />
          <Statistic
            title="Total Add-on Quantity (All)"
            value={totalQuantityAll}
            valueStyle={{ color: "#52c41a" }}
          />
          <Statistic
            title="Current Page Add-on Quantity"
            value={totalQuantityThisPage}
            valueStyle={{ color: "#faad14" }}
          />
        </div>

        {/* Table */}
        <ProblematicStockTable
          dataSource={stockData}
          loading={loading}
          pagination={{
            current: paginationInfo.current,
            pageSize: paginationInfo.pageSize,
            total: paginationInfo.total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} items`,
          }}
          onChange={handleTableChange}
        />
      </div>
    </div>
  );
};

ProblematicHistoryPage.propTypes = {
  history: PropTypes.object.isRequired,
  userData: PropTypes.object,
};

export default ProblematicHistoryPage;
